import { inject, Injectable } from '@angular/core';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { ReachableAttributeStore } from 'src/app/interfaces/reachable-attribute-store';
import { Rule } from 'src/app/interfaces/rule';
import { ValidAttributeStore } from 'src/app/interfaces/valid-attribute-store';
import { RulesProviderService } from 'src/app/providers/rules/rules.provider.service';
import { WebProviderService } from 'src/app/providers/web/web.provider.service';


@Injectable({
  providedIn: 'root'
})
export class RulesService {
  private reachableForms: number[] | null = null;
  private reachableWidths: number[] | null = null;
  private reachablePrimaryMaterials: number[] | null = null;
  private reachableSecondaryMaterials: number[] | null = null;
  private reachableDesigns: number[] | null = null;
  private reachableSizes: number[] | null = null;
  private reachableStoneTypesWithShapes: number[] | null = null;
  private reachableStoneArrangementsWithSettings: number[] | null = null;
  private reachableStoneSizes: number[] | null = null;

  protected webProvider = inject(WebProviderService);
  private rulesProviderService = inject(RulesProviderService);
  protected get provider(): RulesProviderService {
    return this.rulesProviderService;
  }

  async getIsValid(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<boolean> {
    return this.provider.getIsValid(configuredItem, retailerIframeID);
  }

  async getValidAttributes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<ValidAttributeStore> {
    return this.provider.getValidAttributes(configuredItem, retailerIframeID);
  }

  async getIsSinglePart(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<boolean> {
    return this.provider.getIsSinglePart(configuredItem, retailerIframeID);
  }

  async getSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    return this.provider.getAvailableSizes(configuredItem, retailerIframeID);
  }

  async getOutsideMaterials(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    return this.provider.getAvailableOutsideMaterials(configuredItem, retailerIframeID);
  }

  async getAvailableStoneArrangementsWithSettings(configuredItem: ConfiguredItem, retailerIframeID: string | null) {
    return this.provider.getAvailableStoneArrangementsWithSettings(configuredItem, retailerIframeID);
  }

  async getAvailableStoneSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null) {
    return this.provider.getAvailableStoneSizes(configuredItem, retailerIframeID);
  }

  async getAvailableStoneTypesWithShapes(configuredItem: ConfiguredItem, retailerIframeID: string | null) {
    return this.provider.getAvailableStoneTypesWithShapes(configuredItem, retailerIframeID);
  }

  async getClosestValidItemByDesign(configuredItem: ConfiguredItem, designID: number, retailerIframeID: string | null): Promise<Rule> {
    return this.provider.getClosestValidItemByDesign(configuredItem, designID, retailerIframeID);
  }

  async getClosestValidItemBySize(configuredItem: ConfiguredItem, sizeID: number, retailerIframeID: string | null): Promise<Rule> {
    return this.provider.getClosestValidItemBySize(configuredItem, sizeID, retailerIframeID);
  }

  async getClosestValidItemByForm(configuredItem: ConfiguredItem, formID: number, retailerIframeID: string | null): Promise<Rule> {
    return this.provider.getClosestValidItemByForm(configuredItem, formID, retailerIframeID);
  }

  async getClosestValidItemByWidth(configuredItem: ConfiguredItem, widthID: number, retailerIframeID: string | null): Promise<Rule> {
    return this.provider.getClosestValidItemByWidth(configuredItem, widthID, retailerIframeID);
  }

  async getClosestValidItemByPrimaryMaterial(configuredItem: ConfiguredItem, materialID: number, retailerIframeID: string | null): Promise<Rule>  {
    return this.provider.getClosestValidItemByPrimaryMaterial(configuredItem, materialID, retailerIframeID);
  }

  async getClosestValidItemBySecondaryMaterial(configuredItem: ConfiguredItem, materialID: number, retailerIframeID: string | null): Promise<Rule> {
    return this.provider.getClosestValidItemBySecondaryMaterial(configuredItem, materialID, retailerIframeID);
  }

  async getClosestValidItemByStoneArrangementWithSetting(configuredItem: ConfiguredItem, stoneArrangementWithSettingID: number, retailerIframeID: string | null) {
    return this.provider.getClosestValidItemByStoneArrangementWithSetting(configuredItem, stoneArrangementWithSettingID, retailerIframeID);
  }

  async getClosestValidItemByStoneSize(configuredItem: ConfiguredItem, stoneSizeID: number, retailerIframeID: string | null) {
    return this.provider.getClosestValidItemByStoneSize(configuredItem, stoneSizeID, retailerIframeID);
  }

  async getClosestValidItemByStoneTypeWithShape(configuredItem: ConfiguredItem, stoneTypeWithShapeID: number, retailerIframeID: string | null) {
    return this.provider.getClosestValidItemByStoneTypeWithShape(configuredItem, stoneTypeWithShapeID, retailerIframeID);
  }

  async getReachableAttributes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<ReachableAttributeStore> {
    return this.provider.getReachableAttributes(configuredItem, retailerIframeID);
  }

  async getReachableForms(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    if (!this.reachableForms) {
      this.reachableForms = await this.provider.getReachableForms(configuredItem, retailerIframeID);
    }
    return this.reachableForms;
  }

  async getReachableWidths(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    if (!this.reachableWidths) {
      this.reachableWidths = await this.provider.getReachableWidths(configuredItem, retailerIframeID);
    }
    return this.reachableWidths;
  }

  async getReachablePrimaryMaterials(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    if (!this.reachablePrimaryMaterials) {
      this.reachablePrimaryMaterials = await this.provider.getReachablePrimaryMaterials(configuredItem, retailerIframeID);
    }
    return this.reachablePrimaryMaterials;
  }

  async getReachableSecondaryMaterials(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    if (!this.reachableSecondaryMaterials) {
      this.reachableSecondaryMaterials = await this.provider.getReachableSecondaryMaterials(configuredItem, retailerIframeID);
    }
    return this.reachableSecondaryMaterials;
  }

  async getReachableDesigns(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    if (!this.reachableDesigns) {
      this.reachableDesigns = await this.provider.getReachableDesigns(configuredItem, retailerIframeID);
    }
    return this.reachableDesigns;
  }

  async getReachableSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null): Promise<number[]> {
    if (!this.reachableSizes) {
      this.reachableSizes = await this.provider.getReachableSizes(configuredItem, retailerIframeID);
    }
    return this.reachableSizes;
  }

  async getReachableStoneTypesWithShapes(configuredItem: ConfiguredItem, retailerIframeID: string | null) {
    if (!this.reachableStoneTypesWithShapes) {
      this.reachableStoneTypesWithShapes = await this.provider.getReachableStoneTypesWithShapes(configuredItem, retailerIframeID);
    }
    return this.reachableStoneTypesWithShapes;
  }

  async getReachableStoneArrangementsWithSettings(configuredItem: ConfiguredItem, retailerIframeID: string | null) {
    if (!this.reachableStoneArrangementsWithSettings) {
      this.reachableStoneArrangementsWithSettings = await this.provider.getReachableStoneArrangementsWithSettings(configuredItem, retailerIframeID);
    }
    return this.reachableStoneArrangementsWithSettings;
  }

  async getReachableStoneSizes(configuredItem: ConfiguredItem, retailerIframeID: string | null) {
    if (!this.reachableStoneSizes) {
      this.reachableStoneSizes = await this.provider.getReachableStoneSizes(configuredItem, retailerIframeID);
    }
    return this.reachableStoneSizes;
  }

  invalidateReachableCache() {
    this.reachableForms = null;
    this.reachableWidths = null;
    this.reachablePrimaryMaterials = null;
    this.reachableSecondaryMaterials = null;
    this.reachableDesigns = null;
    this.reachableSizes = null;
    this.reachableStoneTypesWithShapes = null;
    this.reachableStoneArrangementsWithSettings = null;
    this.reachableStoneSizes = null;
  }
}
